import { useCallback, useEffect, useMemo } from 'react';
import { useBulkBroadcastActionsPermission } from 'src/components/common/BulkActions/BulkBroadcastActions/useBulkBroadcastActionsPermission';
import { useAppContext } from 'src/contexts/AppContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { useCommandMenuContext } from 'src/contexts/CommandMenuContext/CommandMenuContext';
import { DialogId } from 'src/contexts/DialogContext/DialogContext';
import { useDialog } from 'src/contexts/DialogContext/useDialog';
import { useMultiSelectionContext } from 'src/contexts/MultiSelectionContext';
import { BulkRemoveAdminHoldDialog } from 'src/dialogs/BulkEdits/BulkRemoveHoldDialog';
import { useEventCallback } from 'src/hooks/useEventCallback';
import { ContentId } from 'src/utils/constants/contentId';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  BulkEditListingClient,
  BulkEditPreviewWithDetails,
  CommandOptionType,
  Event,
  ListingQuery,
} from 'src/WebApiController';

import { INVENTORY_BULK_UPDATE_ADMIN_HOLD_UPDATE_KEY } from '../../InventoryActionDropdown.constants';
import { BulkActionItem } from '../types';
import { LaunchRemoveHold } from './LaunchRemoveHold';

export const useBulkRemoveHoldAction = (
  filterQueryWithEventIds: ListingQuery,
  setIsLoading: (v: boolean) => void,
  isLoading?: boolean,
  event?: Event
) => {
  const { registerCommand, unregisterCommand } = useCommandMenuContext();
  const { hasAdminHoldPermission } = useBulkBroadcastActionsPermission();

  const { activeAccountWebClientConfig } = useAppContext();
  const updateKey = event
    ? `adminHold-event-${event.virtId}`
    : INVENTORY_BULK_UPDATE_ADMIN_HOLD_UPDATE_KEY;
  const {
    refreshCatalog,
    eventsExpansion: { setListItemExpansion, refreshExpandedListItems },
  } = useCatalogDataContext();
  const { showErrorDialog } = useClientLogContext();
  const { setSelectionMode } = useMultiSelectionContext();

  const {
    openDialog: openBulkRemoveHoldDialog,
    closeDialog: closeBulkRemoveHoldDialog,
  } = useDialog(DialogId.BulkRemoveHold, BulkRemoveAdminHoldDialog);

  const onSubmit = useCallback(
    async (
      supportBackgroundProcess?: boolean,
      onPreviewReceived?: (preview: BulkEditPreviewWithDetails) => void,
      preview?: BulkEditPreviewWithDetails
    ) => {
      setIsLoading(true);
      if (onPreviewReceived) {
        await tryInvokeApi(
          async () => {
            const preview = await new BulkEditListingClient(
              activeAccountWebClientConfig
            ).bulkUpdateAdminHoldPreview(filterQueryWithEventIds);

            onPreviewReceived(preview);
          },
          (error) => {
            showErrorDialog('bulkUpdateAdminHoldPreview', error, {
              trackErrorData: { filterQueryWithEventIds, removeHold: true },
            });
          },
          () => {
            if (!supportBackgroundProcess) {
              setIsLoading(false);
            }
          }
        );
      } else {
        await tryInvokeApi(
          async () => {
            const succeeded = await new BulkEditListingClient(
              activeAccountWebClientConfig
            ).bulkUpdateAdminHold(
              preview!.preview,
              null,
              null,
              updateKey,
              supportBackgroundProcess
            );
            if (!supportBackgroundProcess) {
              if (succeeded) {
                if (!event) {
                  await refreshCatalog();
                }
                setSelectionMode();
                await refreshExpandedListItems();
              }
            }
          },
          (error) => {
            showErrorDialog(
              'BulkEditListingClient.bulkUpdateAdminHold',
              error,
              {
                trackErrorData: { preview, removeHold: true },
              }
            );
          },
          () => {
            if (!supportBackgroundProcess) {
              setIsLoading(false);
            }
          }
        );
      }
    },
    [
      activeAccountWebClientConfig,
      event,
      filterQueryWithEventIds,
      refreshCatalog,
      refreshExpandedListItems,
      setIsLoading,
      setSelectionMode,
      showErrorDialog,
      updateKey,
    ]
  );

  const openDialogStableRef = useEventCallback(() => {
    if (event) {
      setListItemExpansion(true, [event.virtId]);
    }
    openBulkRemoveHoldDialog({
      updateKey,
      onOkay: onSubmit,
      onCancel: closeBulkRemoveHoldDialog,
      onClosed: () => {
        setIsLoading(false);
      },
    });
  });

  useEffect(() => {
    registerCommand({
      command: {
        id: CommandOptionType.ListingBulkEditRemoveHold,
        onSelect: openDialogStableRef,
      },
      disabled: !hasAdminHoldPermission,
    });

    return () => {
      unregisterCommand({
        commandId: CommandOptionType.ListingBulkEditRemoveHold,
        disabled: !hasAdminHoldPermission,
      });
    };
  }, [
    hasAdminHoldPermission,
    openDialogStableRef,
    registerCommand,
    unregisterCommand,
  ]);

  const onClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (event) {
        setListItemExpansion(true, [event.virtId]);
      }
      openBulkRemoveHoldDialog({
        updateKey,
        onOkay: onSubmit,
        onCancel: closeBulkRemoveHoldDialog,
        onClosed: () => {
          setIsLoading(false);
        },
      });
    },
    [
      closeBulkRemoveHoldDialog,
      event,
      onSubmit,
      openBulkRemoveHoldDialog,
      setIsLoading,
      setListItemExpansion,
      updateKey,
    ]
  );

  const actionItems = useMemo<BulkActionItem[]>(() => {
    if (!hasAdminHoldPermission) {
      return [];
    }
    return [
      {
        title: ContentId.RemoveHold,
        onClick,
        disabled: isLoading,
      },
    ];
  }, [hasAdminHoldPermission, isLoading, onClick]);

  return {
    dropDown: (
      <LaunchRemoveHold
        key="RemoveHold"
        onClick={onClick}
        disabled={isLoading}
      />
    ),
    actionItems,
  };
};
